import { track } from '@amplitude/analytics-browser';
import {
  Application,
  ApplicationAction,
  ApplicationActionButtonType,
  ApplicationStatus,
} from '@features/applications';
import { getStatusChipLabel } from '@features/applications/components/ApplicationListItem/utils';
import { applicationActionButtons } from '@features/applications/components/ApplicationViewActions/constants';
import { applicationActions } from '@features/applications/components/BulkActions/constants';
import {
  getCTAButtonColor,
  getEndpointForApplicationAction,
  getSendWithMessageButtonText,
  getSendWithoutMessageButtonText,
  getSuccessMessage,
  getTitle,
} from '@features/applications/components/BulkActions/utils';
import ChooseInterviewStyleModal from '@features/applications/components/ChooseInterviewStyleModal/ChooseInterviewStyleModal';
import HireflixInterviewModal from '@features/applications/components/HireflixInterviewModal/HireflixInterviewModal';
import SingleMessageModal from '@features/applications/components/SingleMessageModal/SingleMessageModal';
import { useDeleteHireflixInterview } from '@features/applications/hooks/useDeleteHireflixInterview';
import { useEnterpriseBulkAction } from '@features/applications/hooks/useEnterpriseBulkAction';

import { useHireflixInviteToInterview } from '@features/applications/hooks/useHireflixInviteToInterview';
import { useInviteToInterview } from '@features/applications/hooks/useInviteToInterview';
import { useInviteToInterviewBlocks } from '@features/applications/hooks/useInviteToInterviewBlocks';
import { useSingleApplicationAction } from '@features/applications/hooks/useSingleApplicationAction';
import EmployerTerminationModal from '@features/employerTeam/components/EmployerTerminationModal';
import MessagingModal from '@features/messaging/components/MessagingModal/MessagingModal';
import { Button, Icon, Stack } from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { white } from '@styles/colors';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { selectedPlanName } from '@utils/pricing';
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { toast } from 'react-toastify';

type ApplicationViewActionsProps = {
  application: Application;
  selectedCompanyId?: string | number;
  setApplicationData?: Dispatch<SetStateAction<Application[] | null>>;
  setApplication: Dispatch<SetStateAction<Application | null>>;
  handlePageNo?: (newPageNo: number) => void;
};

const ApplicationViewActions: FC<ApplicationViewActionsProps> = ({
  application,
  selectedCompanyId,
  setApplicationData,
  setApplication,
  handlePageNo,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const [errorText, setErrorText] = useState('');
  const [selectedAction, setSelectedAction] =
    useState<ApplicationAction | null>(null);
  const [isSelectInterviewStyleModalOpen, setIsSelectInterviewStyleModalOpen] =
    useState(false);
  const [isHireflixInterviewModalOpen, setIsHireflixInterviewModalOpen] =
    useState(false);
  const [isRescheduleInterview, setIsRescheduleInterview] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [isDateRangePickerShown, setIsDateRangePickerShown] = useState(false);
  const [startTimestamp, setStartTimestamp] = useState<number | null>(null);
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [formattedDate, setFormattedDate] = useState<string>('');
  const [isPastDateTime, setIsPastDateTime] = useState(false);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const applicationStatusLabel = getStatusChipLabel(
    application.last_action || 'unattended',
    application,
  );

  const [defaultType, setDefaultType] = useState<
    'inPerson' | 'virtual' | 'hireflix'
  >('inPerson');

  const showOnlyMessage =
    application.employer_archive ||
    application.withdrawn ||
    applicationStatusLabel === 'HIRED' ||
    applicationStatusLabel === 'resigned' ||
    applicationStatusLabel === 'terminated';
  const showTerminateButton = applicationStatusLabel === 'HIRED';
  const { createApplicationAction, isCreateApplicationActionLoading } =
    useSingleApplicationAction({ setErrorText });

  const { inviteToInterviewBlocks, isInviteToInterviewBlocksLoading } =
    useInviteToInterviewBlocks({ setErrorText });

  const { hireflixInviteToInterview, isHireflixInviteToInterviewLoading } =
    useHireflixInviteToInterview({ setErrorText });

  const { deleteHireflixInterview } = useDeleteHireflixInterview({
    setErrorText,
  });

  const onInterviewInviteSuccess = () => {
    setSelectedAction(null);
    setIsSelectInterviewStyleModalOpen(false);
    setIsRescheduleInterview(false);
    toast.success('Interview invite sent');
  };

  const { inviteToInterview, isInviteToInterviewLoading } =
    useInviteToInterview({ setErrorText });

  const getEventType = (actionType: ApplicationActionButtonType) => {
    switch (actionType) {
      case 'save':
        return `${amplitudeEventsName.APPLICANTS_CLICK_SAVE_APPLICANT}-${selectedPlan}`;
      case 'hire':
        return `${amplitudeEventsName.APPLICANTS_CLICK_SEND_OFFER}-${selectedPlan}`;
      case 'decline':
        return `${amplitudeEventsName.APPLICANTS_CLICK_DECLINED}-${selectedPlan}`;
      case 'archive':
        return `${amplitudeEventsName.APPLICANTS_CLICK_ARCHIVE}-${selectedPlan}`;
      case 'invite to interview block':
        return `${amplitudeEventsName.APPLICANTS_CLICK_INTERVIEW_APPLICANT}-${selectedPlan}`;
      case 'messenger':
        return `${amplitudeEventsName.APPLICANTS_CLICK_MESSAGE_APPLICANT}-${selectedPlan}`;
    }
  };

  const handleActionClick = (
    actionType: ApplicationActionButtonType,
    reshedule?: boolean,
  ) => {
    const eventType = getEventType(actionType);
    if (isCompany && eventType) {
      track(eventType);
    }
    switch (actionType) {
      case 'save':
      case 'hire':
      case 'decline':
      case 'archive':
        return setSelectedAction(
          applicationActions.find((item) => item.actionType === actionType)!,
        );
      case 'invite to interview block':
        return (
          setIsSelectInterviewStyleModalOpen(true),
          reshedule && setIsRescheduleInterview(true)
        );
      case 'messenger':
        return setIsMessagingModalOpen(true);
      default:
    }
  };

  const handleInviteToInterviewSubmit = (message: string) => {
    if (defaultType === 'hireflix') {
      return hireflixInviteToInterview(
        {
          application_id: application.id,
          body: message,
        },
        {
          onSuccess: (data) => {
            if (data?.status === 200) {
              setStartTimestamp(null);
              setEndTimestamp(null);
              onInterviewInviteSuccess();
              setIsDateRangePickerShown(false);
              setApplication(data?.object?.application);
              setApplicationData &&
                setApplicationData((prev) => {
                  if (!prev) return null;
                  return prev?.map((application) => {
                    if (application.id === data?.object?.application.id) {
                      return data?.object?.application;
                    }
                    return application;
                  });
                });
            }
          },
        },
      );
    }

    if (isDateRangePickerShown) {
      return inviteToInterview(
        {
          block_type: defaultType === 'inPerson' ? 1 : 0,
          physical_interview_address:
            authData?.user?.company?.additional_info
              ?.physical_interview_address,
          physical_interview_location:
            authData?.user?.company?.additional_info
              ?.physical_interview_location,
          virtual_interview_link:
            authData?.user?.company?.additional_info?.virtual_interview_link,
          invitee_id: application.applicant.id,
          application_id: application.id,
          body: message,
          start_time: startTimestamp && startTimestamp,
          end_time: endTimestamp && endTimestamp,
          eventDate: formattedDate,
          position: application.position,
          ...(isRescheduleInterview || application.interview_action
            ? { is_reschedule: true }
            : {}),
        },
        {
          onSuccess: (data) => {
            if (data?.status == 200) {
              setStartTimestamp(null);
              setEndTimestamp(null);
              onInterviewInviteSuccess();
              setIsDateRangePickerShown(false);
              setApplication(data?.object?.application);
              setApplicationData &&
                setApplicationData((prev) => {
                  if (!prev) return null;
                  return prev?.map((application) => {
                    if (application.id === data?.object?.application.id) {
                      return data?.object?.application;
                    }
                    return application;
                  });
                });
            }
          },
        },
      );
    }

    return inviteToInterviewBlocks(
      {
        block_type: defaultType === 'inPerson' ? 1 : 0,
        physical_interview_address:
          authData?.user?.company?.additional_info?.physical_interview_address,
        physical_interview_location:
          authData?.user?.company?.additional_info?.physical_interview_location,
        virtual_interview_link:
          authData?.user?.company?.additional_info?.virtual_interview_link,
        user_id: application.applicant.id,
        application_id: application.id,
        ...(selectedAction?.actionType === 'Invite to Open Interview' && {
          slot_interview: false,
        }),
        ...(selectedAction?.actionType === 'invite to interview block' && {
          slot_interview: true,
        }),
        body: message,
        ...(isRescheduleInterview || application.interview_action
          ? { is_reschedule: true }
          : {}),
      },
      {
        onSuccess: (data) => {
          if (data?.status == 200) {
            setStartTimestamp(null);
            setEndTimestamp(null);
            onInterviewInviteSuccess();
            setApplication(data?.object);
            setApplicationData &&
              setApplicationData((prev) => {
                if (!prev) return null;
                return prev?.map((application) => {
                  if (application.id === data?.object?.id) {
                    return data?.object;
                  }
                  return application;
                });
              });
          }
        },
      },
    );
  };

  const handleSend = (isMessageRequired: boolean, message: string) => {
    switch (selectedAction?.actionType) {
      case 'save':
      case 'hire':
      case 'decline':
      case 'archive':
        return createApplicationAction(
          {
            endpoint: getEndpointForApplicationAction(selectedAction),

            payload: {
              application_id: application.id,
              ...(isMessageRequired && { message }),
            },
          },
          {
            onSuccess: (data) => {
              if (data?.status === 200) {
                toast.success(
                  getSuccessMessage(isMessageRequired, selectedAction!),
                );
                setApplication(data?.object);
                setIsDateRangePickerShown(false);

                setApplicationData &&
                  setApplicationData((prev) => {
                    if (!prev) return null;
                    return prev.map((application) =>
                      application.id === data?.object.id
                        ? data?.object
                        : application,
                    );
                  });

                // ✅ If declined and the application has a HireFlix interview — delete it
                if (
                  selectedAction?.actionType === 'decline' &&
                  application?.hire_flix_interview
                ) {
                  deleteHireflixInterview({ application_id: application.id });
                }

                setSelectedAction(null);
              }
            },
          },
        );

      case 'invite to interview block':
      case 'Invite to Open Interview':
      case 'Scheduled Interview':
      case 'invite to hireflix interview':
        return handleInviteToInterviewSubmit(message);

      default:
        break;
    }
  };

  const { enterpriseCreateBulkAction, isEnterpriseCreateBulkActionLoading } =
    useEnterpriseBulkAction({
      setErrorText,
    });

  const handleSendEnterprise = async (
    isMessageRequired: Boolean,
    message: string,
  ) => {
    const bulkActionPayload = {
      action: selectedAction?.actionType.toString(),
      ids: application?.id.toString(),
      ...(isMessageRequired && { message }),
      ...(isRescheduleInterview || application.interview_action
        ? { is_reschedule: true }
        : {}),
      ...(isDateRangePickerShown &&
        selectedAction?.actionType === 'Scheduled Interview' && {
          block_type: defaultType === 'inPerson' ? 1 : 0,
          start_time: startTimestamp && startTimestamp,
          end_time: endTimestamp && endTimestamp,
          eventDate: formattedDate,
        }),
      ...(selectedAction?.actionType === 'Invite to Open Interview' && {
        block_type: defaultType === 'inPerson' ? 1 : 0,
        slot_interview: false,
      }),
      ...(selectedAction?.actionType === 'invite to interview block' && {
        block_type: defaultType === 'inPerson' ? 1 : 0,
        slot_interview: true,
      }),
    };

    enterpriseCreateBulkAction(bulkActionPayload, {
      onSuccess: (data) => {
        if (data?.status === 200) {
          setStartTimestamp(null);
          setEndTimestamp(null);
          const updatedSelectedApplication = data?.object.find(
            (application: { id: any }) => application.id === application?.id,
          );
          setApplication(updatedSelectedApplication);
          setIsDateRangePickerShown(false);
          setApplicationData &&
            setApplicationData((prev) => {
              if (!prev) return null;
              return prev?.map((application) => {
                if (application.id === updatedSelectedApplication.id) {
                  return updatedSelectedApplication;
                }
                return application;
              });
            });
          toast.success(getSuccessMessage(isMessageRequired, selectedAction!));
          setIsSelectInterviewStyleModalOpen(false);
          setIsRescheduleInterview(false);
          setSelectedAction(null);
        }
      },
    });
  };

  return (
    <Stack
      spacing={2}
      bgcolor={white}
      borderRadius="8px"
      py={3}
      px={2}
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
    >
      {showOnlyMessage ? (
        <>
          <Button
            key="Messenger"
            variant="contained"
            size="small"
            onClick={() => handleActionClick('messenger')}
            startIcon={<Icon fontSize="small" />}
            color="primary"
            sx={{
              fontSize: '13px',
              flexGrow: 1,
            }}
          >
            Message
          </Button>
          {showTerminateButton && (
            <Button
              key="Terminate"
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowTerminateModal(true);
              }}
              startIcon={<Icon fontSize="small" />}
              color="error"
              sx={{
                fontSize: '13px',
                flexGrow: 1,
              }}
            >
              Terminate
            </Button>
          )}
        </>
      ) : (
        applicationActionButtons
          .filter(({ hideOnStatus, label }) => {
            const currentStatus = (
              application?.last_action
                ? application?.last_action?.toLowerCase()
                : 'unattended'
            ) as ApplicationStatus;

            // Default hide conditions
            const isHiddenByStatus = hideOnStatus?.includes(currentStatus);

            // Custom condition: hide "Reschedule interview" if hire_flix_interview exists
            const isRescheduleInterviewHidden =
              label === 'Reschedule interview' &&
              application?.hire_flix_interview;

            return !isHiddenByStatus && !isRescheduleInterviewHidden;
          })
          .map(({ label, color, Icon, actionType }) => (
            <Button
              key={label}
              variant="contained"
              size="small"
              onClick={() =>
                handleActionClick(actionType, label === 'Reschedule interview')
              }
              startIcon={<Icon fontSize="small" />}
              color={color}
              sx={{
                fontSize: '13px',
                flexGrow: 1,
              }}
            >
              {label}
            </Button>
          ))
      )}

      {Boolean(selectedAction) && (
        <SingleMessageModal
          setDefaultType={setDefaultType}
          defaultType={defaultType}
          isOpen={Boolean(selectedAction)}
          handleClose={() => {
            setSelectedAction(null);
            setIsDateRangePickerShown(false);
            setStartTimestamp(null);
            setEndTimestamp(null);
            setFormattedDate('');
            setErrorText('');
          }}
          action={selectedAction!}
          selectedCompanyId={selectedCompanyId}
          title={getTitle(selectedAction!)}
          subtitle={`${application?.applicant?.name} - ${application.position}`}
          isSubtitleShown
          submitWithMessageText={getSendWithMessageButtonText(selectedAction!)}
          submitWithoutMessageText={getSendWithoutMessageButtonText(
            selectedAction!,
          )}
          showSendWithoutMessage={selectedAction?.showSendWithoutMessage}
          errorText={errorText}
          setErrorText={setErrorText}
          buttonColor={getCTAButtonColor(selectedAction?.title)}
          handleSubmit={
            authData.user?.enterprise ? handleSendEnterprise : handleSend
          }
          disabled={
            isCreateApplicationActionLoading ||
            isEnterpriseCreateBulkActionLoading ||
            isInviteToInterviewBlocksLoading ||
            isInviteToInterviewLoading ||
            (isDateRangePickerShown &&
              (!startTimestamp || !endTimestamp || isPastDateTime))
          }
          isDateRangePickerShown={isDateRangePickerShown}
          setStartTimestamp={setStartTimestamp}
          setEndTimestamp={setEndTimestamp}
          setFormattedDate={setFormattedDate}
          setIsPastDateTime={setIsPastDateTime}
          hireflixInviteLoading={isHireflixInviteToInterviewLoading}
          isInterviewExist={Boolean(
            application?.calendar_block_invite || application?.calendar_item,
          )}
        />
      )}

      {isSelectInterviewStyleModalOpen && (
        <ChooseInterviewStyleModal
          isOpen={isSelectInterviewStyleModalOpen}
          handleClose={() => {
            setIsSelectInterviewStyleModalOpen(false);
            setIsRescheduleInterview(false);
          }}
          subtitle={`${application?.applicant?.name} - ${application.position}`}
          companyId={application?.job_listing?.company?.id}
          jobId={application?.job_listing?.id}
          inviteToInterview={(inviteType: number) => {
            const actionType =
              inviteType === 0
                ? 'invite to interview block'
                : 'Invite to Open Interview';

            const action = applicationActions.find(
              (item) => item.actionType === actionType,
            );

            if (action) {
              setSelectedAction(action);
            }
          }}
          scheduleInterview={() => {
            setIsDateRangePickerShown(true);
            setSelectedAction(
              applicationActions.find(
                (item) => item.actionType === 'Scheduled Interview',
              )!,
            );
          }}
          setIsHireflixInterviewModalOpen={setIsHireflixInterviewModalOpen}
          isInterviewExist={Boolean(
            application?.calendar_block_invite || application?.calendar_item,
          )}
        />
      )}

      {isMessagingModalOpen && (
        <MessagingModal
          isOpen={isMessagingModalOpen}
          handlePageNo={handlePageNo}
          handleClose={() => setIsMessagingModalOpen(false)}
          application={application}
        />
      )}

      {showTerminateModal && (
        <EmployerTerminationModal
          isOpen={showTerminateModal}
          handleClose={() => setShowTerminateModal(false)}
          application={application}
          setApplicationData={setApplicationData}
          setApplication={setApplication}
        />
      )}

      {isHireflixInterviewModalOpen && (
        <HireflixInterviewModal
          application={application}
          subtitle={`${application?.applicant?.name} - ${application.position}`}
          selectedCompanyId={selectedCompanyId}
          jobId={application?.job_listing?.id}
          isOpen={isHireflixInterviewModalOpen}
          handleClose={() => setIsHireflixInterviewModalOpen(false)}
        />
      )}
    </Stack>
  );
};

export default ApplicationViewActions;
